import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { DocumentType, LoadingStatus, ViewExtension } from "../../../@types/enumsGlobal";
import { IEntitySimpleDTO } from "../../../libs/coreapi-dto/@types/common";
import { ContractorDataProvider } from "../../../Services/DataProviders/ContractorDataProvider";
import { IContractorDTO } from "../../../libs/coreapi-dto/dirs/contractor";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import { GlobalImportInvoicesDataProvider } from "../../../Services/DataProviders/GlobalImportInvoicesDataProvider";
import { ImportedInvoiceDataProvider } from "../../../Services/DataProviders/ImportedInvoiceDataProvider";
import { IBatchImportResultDTO, IImportInvoicePreviewItemDTO, IImportInvoiceResultDTO, ITypeBunch } from "../../../libs/core-api-requests/dirs/globalImportInvoicesRequest";
import useGridFilter, { DefaultDateGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import styles from "../../styles/index.module.scss";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import localStyles from "./GlobalImportInvoicesView.module.scss";
import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import { IImportFileViewDTO, IImportFilesFilterDTO, ILocalFileDTO, LocalFileStatus } from "../../../libs/coreapi-dto/dirs/globalImportInvoices";
import { IInvoiceImportedDTO } from "../../../libs/coreapi-dto/documents/importedInvoice";
import { BunchType, IImportedInvoiceItemDTO, normalizeImportedInvoiceItem } from "../../../libs/coreapi-dto/documents/importedInvoiceItem";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { GlobalImportInvoicesFilteringPanel } from "./GlobalImportInvoicesFilteringPanel";
import { ConfirmationItemsModal } from "../../../components/modalWindows/ConfirmationItemsModal";
import classNames from "classnames";
import { TabsPanel } from "../../../components/tabs";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import { Button } from "../../../components/buttons/button";
import { Checkbox } from "antd";
import { IDataForMoveFiles } from "../../../libs/coreapi-dto/dirs/importInvoices";

import { CheckIcon } from "../../../libs/corporate/icons/outlined/suggested/SymbolCollection";
import { CrossIcon } from "../../../libs/corporate/icons/outlined/other/OtherCollection";
import { ImportedInvoiceContext } from "./importedInvoiceContext";
import { v4 as uuidv4 } from "uuid";

type DocumentGridStateType = "view" | "create" | "edit" | "copy" | "delete" | "restore" | "restoreAll" | "return" | "search" | "refresh" | "process" | "unprocess" | "unprocessHard" | "cancel" | "confirm" | "confirmDeduction";

interface ISaveFocus {
    viewState: DocumentGridStateType;
    selectedItem: IGridRow | undefined;
    data: any;
}


const GlobalImportInvoiceView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();
    const tabsCtx = useTabsContext();

    const dataProvider = new GlobalImportInvoicesDataProvider(appContext.coreApiService);
    const importedInvoiceDataProvider = new ImportedInvoiceDataProvider(appContext.coreApiService);
    const contractorDataProvider = new ContractorDataProvider(appContext.coreApiService);

    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultDateGridFilter());
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.InProcess);

    const [data, setData] = useState<IImportFileViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [rowImportStatus, setRowImportStatus] = useState<Record<string, "processing" | "success" | "error" | "bunch">>({});
    const [totalToProcess, setTotalToProcess] = useState<number>(0);
    const [progressCounters, setProgressCounters] = useState<{ processed: number; success: number; failed: number }>({ processed: 0, success: 0, failed: 0 });

    const { t } = useTranslation();

    const getImportRowKey = (file: ILocalFileDTO) => `${file.fileName}|${file.idContractorGlobal}|${file.connectionString}`;

    const getMatchingStatus = (item: IImportFileViewDTO): "pending" | "done" | "none" => {
        if (item.idInvoiceGlobal) return "done";
        if (item.idImportedInvoiceGlobal) return "pending";
        return "none";
    };

    const getMatchingStatusDisplay = (status: "pending" | "done" | "none") => {
        if (status === "pending") return t("documents.globalImportInvoices.matchingStatus.pending");
        if (status === "done") return t("documents.globalImportInvoices.matchingStatus.done");
        return "";
    };

    const toGridRow = (item: IImportFileViewDTO) => {
        const statusKey = (item.status as string)?.toLowerCase() ?? "";
        const idGlobal = getImportRowKey(item);
        const matchingStatus = getMatchingStatus(item);
        return {
            ...item,
            idGlobal,
            displayName: item.fileName,
            statusDisplay: statusKey ? t(`documents.globalImportInvoices.status.${statusKey}`) : item.status,
            statusOriginal: item.status,
            importStatus: rowImportStatus[idGlobal],
            matchingStatus,
            matchingStatusDisplay: getMatchingStatusDisplay(matchingStatus)
        };
    };

    const gridData = useMemo(() => {
        return data.map(toGridRow);
    }, [data, t, rowImportStatus]);

    const multipleSelect = true;
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [confirmationItemsModalJsx, setConfirmationItemsModalJsx] = useState<JSX.Element>(<></>);

    const [selectedImportedInvoice, setSelectedImportedInvoice] = useState<IInvoiceImportedDTO | null>(null);
    const [selectedImportedInvoiceLoading, setSelectedImportedInvoiceLoading] = useState<boolean>(false);

    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [showDetailsTabs, setShowDetailsTabs] = useState<boolean>(false);

    const [saveFocus, setSaveFocus] = useState<ISaveFocus>({
        viewState: "view",
        selectedItem: selectedItem,
        data: data
    });

    const [datePeriod, setDatePeriod] = useState<IDatePeriod>({
        startDate: DateTime.now().minus({ days: 7 }).startOf("day"),
        endDate: DateTime.now().endOf("day")
    });

    const [statusFilter, setStatusFilter] = useState<LocalFileStatus | undefined>(LocalFileStatus.New);
    const [contractorFilter, setContractorFilter] = useState<IEntitySimpleDTO | null>(null);
    const [supplierFilter, setSupplierFilter] = useState<IEntitySimpleDTO | null>(null);
    const [invoiceImportMappingModel, setInvoiceImportMappingModel] = useState<IEntitySimpleDTO | null>(null);
    const [supplierImportProvider, setSupplierImportProvider] = useState<string | null>(null);
    const [isInvoiceSettingEnabled, setIsInvoiceSettingEnabled] = useState<boolean>(false);
    const [storeFilter, setStoreFilter] = useState<IEntitySimpleDTO | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [filterErrors, setFilterErrors] = useState<{ pharmacy?: string; store?: string }>({});
    const [showFilterErrors, setShowFilterErrors] = useState<boolean>(false);
    const [skipBunchIfLinked, setSkipBunchIfLinked] = useState<boolean>(() => {
        try {
            const raw = localStorage.getItem("f3tail.globalImportInvoices.skipBunchIfLinked");
            return raw === null ? true : raw === "true";
        } catch {
            return true;
        }
    });
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const refreshData = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    useEffect(() => {
        try {
            localStorage.setItem("f3tail.globalImportInvoices.skipBunchIfLinked", String(skipBunchIfLinked));
        } catch {
            // ignore
        }
    }, [skipBunchIfLinked]);

    const importStats = useRef<{ success: number; failed: number }>({ success: 0, failed: 0 });
    const pendingBunchQueue = useRef<{ file: ILocalFileDTO; idImportedInvoiceGlobal: string; rowId: string; typeBunches?: ITypeBunch[]; errorsList?: string[] }[]>([]);
    const fileByIdGlobal = useRef<Map<string, IImportFileViewDTO>>(new Map());
    const previewRequestedRef = useRef<Set<string>>(new Set());
    const abortControllerRef = useRef<AbortController | null>(null);
    const isCancelledRef = useRef<boolean>(false);
    const selectedIdByFileName = useRef<Map<string, string>>(new Map());

    const mapDtoToLocalFile = (dto: IImportFileViewDTO): ILocalFileDTO => ({
        fileName: dto.fileName,
        idContractorGlobal: dto.idContractorGlobal,
        contractorName: dto.contractorName,
        connectionString: dto.connectionString,
        idSupplierGlobal: dto.idSupplierGlobal,
        supplierName: dto.supplierName,
        idStoreGlobal: dto.idStoreGlobal,
        storeName: dto.storeName,
        supplierDocNum: dto.supplierDocNum,
        supplierDocDate: dto.supplierDocDate,
        idImportedInvoiceGlobal: dto.idImportedInvoiceGlobal,
        idInvoiceGlobal: dto.idInvoiceGlobal,
        status: dto.status,
        itemCount: dto.itemCount,
        sumIncVat: dto.sumIncVat,
        sumWithoutVat: dto.sumWithoutVat,
        vatSum: dto.vatSum
    });

    const enrichFileDto = (dto: IImportFileViewDTO): IImportFileViewDTO => ({
        ...dto,
        idContractorGlobal: dto.idContractorGlobal || contractorFilter?.idGlobal || "",
        idStoreGlobal: dto.idStoreGlobal || storeFilter?.idGlobal || null
    });

    const handleInvoiceImportMappingModelChange = (value: IEntitySimpleDTO | null) => {
        setInvoiceImportMappingModel(value);
        if (!supplierFilter?.idGlobal) return;

        contractorDataProvider.getById(supplierFilter.idGlobal, (contractor) => {
            if (!contractor) return;
            const updated: IContractorDTO = { ...contractor, invoiceImportMappingModel: (value as any) ?? undefined };
            contractorDataProvider.update(supplierFilter.idGlobal, updated);
        });
    };

    const isDestinationSelected = !!contractorFilter?.idGlobal && !!storeFilter?.idGlobal;

    const validateFilter = (contractor: IEntitySimpleDTO | null, store: IEntitySimpleDTO | null) => {
        const errors: { pharmacy?: string; store?: string } = {};
        if (!contractor?.idGlobal) {
            errors.pharmacy = "Не выбрана аптека";
        }
        if (!store?.idGlobal) {
            errors.store = "Не выбран склад";
        }
        setFilterErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const getFilter = (): IImportFilesFilterDTO => ({
        pageNumber: gridFilter.pageNumber,
        numberPerPage: gridFilter.numberPerPage,
        fieldOrderBy: gridFilter.fieldOrderBy,
        orderDesc: gridFilter.orderDesc,
        idContractorGlobal: null,
        idSupplierGlobal: supplierFilter?.idGlobal ?? null,
        idStoreGlobal: null,
        search: searchText ?? "",
        supplierDocDateFrom: datePeriod.startDate,
        supplierDocDateTo: datePeriod.endDate,
        status: statusFilter ?? null
    });

    useEffect(() => {
        validateFilter(contractorFilter, storeFilter);
    }, [contractorFilter, storeFilter]);

    useEffect(() => {
        if (!supplierFilter?.idGlobal) {
            setInvoiceImportMappingModel(null);
            setSupplierImportProvider(null);
            setIsInvoiceSettingEnabled(false);
            return;
        }

        contractorDataProvider.getById(supplierFilter.idGlobal, (contractor) => {
            if (!contractor) return;
            const provider = contractor.importInvoiceInfo?.importProvider ?? null;
            setSupplierImportProvider(provider);
            setInvoiceImportMappingModel(contractor.invoiceImportMappingModel ?? null);
            const enabled = provider !== ViewExtension.sstProtek && provider !== ViewExtension.xmlCommerceML;
            setIsInvoiceSettingEnabled(enabled);
        });
    }, [supplierFilter]);

    useEffect(() => {
        const map = new Map<string, IImportFileViewDTO>();
        data.forEach((item) => {
            map.set(getImportRowKey(item), item);
        });
        fileByIdGlobal.current = map;
    }, [data]);

    useEffect(() => {
        setIsSubmitting(true);
        dataProvider.getImportFilesView(getFilter(), (entities, total) => {
            const enriched = entities.map(enrichFileDto);
            setData(enriched);
            setTotalCount(total);
            if (!enriched || enriched.length === 0) {
                setLoadingStatus(LoadingStatus.NoData);
            } else {
                setLoadingStatus(LoadingStatus.Completed);
            }
            setIsSubmitting(false);

            const filesToPreview = enriched
                .filter(e => e.status === LocalFileStatus.New
                    && (e.itemCount == null || e.sumIncVat == null)
                    && !previewRequestedRef.current.has(`${e.fileName}|${e.idContractorGlobal}|${e.connectionString}`))
                .map(mapDtoToLocalFile);

            if (filesToPreview.length > 0) {
                filesToPreview.forEach(f => previewRequestedRef.current.add(`${f.fileName}|${f.idContractorGlobal}|${f.connectionString}`));
                dataProvider.previewBatch(filesToPreview, () => {});
            }
        });
    }, [gridFilter, refreshTrigger, datePeriod, statusFilter, supplierFilter, searchText]);

    

    const renderConfirmationModal = (header: string, warning: string, items: string, question: string, cancel: () => void) => {
        setConfirmationItemsModalJsx(
            <ConfirmationItemsModal
                header={header}
                warning={warning}
                listPositions={items}
                question={question}
                cancel={cancel}
            />
        );
    };

    const getSelectedFiles = (): ILocalFileDTO[] => {
        const rows = multipleSelect && selectedItems.length > 0
            ? selectedItems
            : selectedItem
                ? [selectedItem]
                : [];

        return rows
            .map((row) => fileByIdGlobal.current.get(row.idGlobal))
            .filter((dto): dto is IImportFileViewDTO => !!dto)
            .map(mapDtoToLocalFile);
    };

    const canImportFiles = (files: ILocalFileDTO[]): boolean => {
        if (files.length === 0) {
            renderGlobalAlert({
                variant: "warning",
                statusCode: 0,
                title: "Внимание",
                detail: "Выберите файлы для импорта"
            });
            return false;
        }

        const actionableFiles = files.filter(f => f.status === LocalFileStatus.New);
        const otherInvalidFiles = files.filter(f => f.status !== LocalFileStatus.New && f.status !== LocalFileStatus.Imported);

        if (otherInvalidFiles.length > 0) {
            const details = otherInvalidFiles
                .map(f => `${f.fileName} (статус: ${f.status})`)
                .join("; ");
            renderGlobalAlert({
                variant: "warning",
                statusCode: 0,
                title: "Внимание",
                detail: `Можно импортировать только файлы со статусом "Новый". Следующие файлы не подходят: ${details}`
            });
            return false;
        }

        if (actionableFiles.length === 0) {
            renderGlobalAlert({
                variant: "info",
                statusCode: 0,
                title: "Информация",
                detail: "Выбранные файлы уже импортированы"
            });
            return false;
        }

        if (!isDestinationSelected) {
            renderGlobalAlert({
                variant: "warning",
                statusCode: 0,
                title: "Внимание",
                detail: "Выберите аптеку и склад"
            });
            return false;
        }

        return true;
    };

    const openBunchForFile = (
        file: ILocalFileDTO,
        invoice: IInvoiceImportedDTO,
        rowId?: string,
        onComplete?: (saved: boolean) => void
    ) => {
        const items: IImportedInvoiceItemDTO[] = invoice.items.map((i: any) =>
            normalizeImportedInvoiceItem({
                ...i,
                success: i.success,
                type: i.type
            })
        );

        const dataForMove: IDataForMoveFiles = {
            idContractor: file.idContractorGlobal,
            idSupplier: file.idSupplierGlobal ?? "",
            fileName: [file.fileName],
            idsInvoices: [invoice.idGlobal]
        };

        let processed = false;

        tabsCtx.openChild('bunch_invoices_plugin', 'create', undefined, () => { }, {
            importedInvoiceItems: items,
            idInvoice: invoice.idGlobal,
            isSucceed: invoice.items.every((i: any) => i.success),
            dataForMoveInvoices: dataForMove,
            supplierDocNum: file.supplierDocNum,
            callbackAfterImport: () => refreshData(),
            skipDeleteOnClose: true,
            onClose: () => {
                if (processed) return;
                processed = true;
                onComplete?.(false);
            },
            onSaved: () => {
                if (processed) return;
                processed = true;
                onComplete?.(true);
            }
        });
    };

    const finishImport = (cancelled = false) => {
        setIsImporting(false);
        setSelectedItems([]);
        setSelectedItem(undefined);
        setRowImportStatus({});
        refreshData()

        if (cancelled) {
            return;
        }

        const stats = importStats.current;
        if (stats.failed === 0 && stats.success === 0) {
            renderGlobalAlert({
                variant: "info",
                statusCode: 0,
                title: "Импорт завершён",
                detail: "Нет файлов для отображения результата"
            });
            return;
        }

        if (stats.failed === 0) {
            renderGlobalAlert({
                variant: "success",
                statusCode: 0,
                title: "Успешно",
                detail: `Импортировано накладных: ${stats.success}`
            });
        } else {
            renderGlobalAlert({
                variant: "warning",
                statusCode: 0,
                title: "Импорт завершён с ошибками",
                detail: `Успешно: ${stats.success}. С ошибками: ${stats.failed}`
            });
        }
    };

    const processNextBunch = () => {
        const next = pendingBunchQueue.current.shift();
        if (!next) {
            finishImport();
            return;
        }

        const storeId = next.file.idStoreGlobal || storeFilter?.idGlobal || "";
        dataProvider.loadAndGetByFileName({ file: next.file, store: storeId }, (invoice, errorMessage) => {
            if (isCancelledRef.current) {
                return;
            }

            if (!invoice) {
                importStats.current.failed += 1;
                setProgressCounters(c => ({ ...c, failed: c.failed + 1 }));
                setRowImportStatus(prev => ({ ...prev, [next.rowId]: "error" }));
                const detail = errorMessage?.detail || errorMessage?.title || `Не удалось загрузить импортированную накладную для файла ${next.file.fileName}`;
                renderGlobalAlert({
                    variant: "error",
                    statusCode: errorMessage?.statusCode ?? 0,
                    title: "Ошибка загрузки накладной",
                    detail
                });
                processNextBunch();
                return;
            }

            const errorList = next.errorsList ?? [];
            const errorIds = new Set(errorList.map(e => e.split(' ')[0]).filter(Boolean));
            const typeBunches = next.typeBunches ?? [];

            const enrichedItems = invoice.items.map((i: any) => {
                const bunch = typeBunches.find(tb => tb.idInvoiceItem === i.idGlobal);
                const hasError = errorIds.has(i.idGlobal);
                const isConnected = i.idGoodsExternalGlobal && i.idGoodsExternalGlobal !== "";
                const hasImportInfo = typeBunches.length > 0 || errorList.length > 0;

                return {
                    ...i,
                    success: hasImportInfo ? !hasError : isConnected,
                    type: bunch?.type ?? ""
                };
            }) as IImportedInvoiceItemDTO[];

            const enrichedInvoice: IInvoiceImportedDTO = {
                ...invoice,
                items: enrichedItems
            };

            openBunchForFile(next.file, enrichedInvoice, next.rowId, (saved) => {
                if (saved) {
                    importStats.current.success += 1;
                    setProgressCounters(c => ({ ...c, success: c.success + 1 }));
                    setRowImportStatus(prev => ({ ...prev, [next.rowId]: "success" }));
                    processNextBunch();
                } else {
                    importStats.current.failed += 1;
                    setProgressCounters(c => ({ ...c, failed: c.failed + 1 }));
                    setRowImportStatus(prev => ({ ...prev, [next.rowId]: "error" }));
                    pendingBunchQueue.current = [];
                    finishImport();
                }
            });
        });
    };

    const handleImportResult = (result: IBatchImportResultDTO, files: ILocalFileDTO[], additionalPendingBunches: ILocalFileDTO[] = []) => {
        if (isCancelledRef.current) {
            return;
        }

        setSelectedItems([]);
        setSelectedItem(undefined);
        refreshData()

        importStats.current = { success: 0, failed: 0 };
        pendingBunchQueue.current = [];

        if (result.generalErrors?.length > 0) {
            setIsImporting(false);
            setRowImportStatus(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(id => {
                    if (next[id] === "processing") {
                        next[id] = "error";
                    }
                });
                return next;
            });
            setProgressCounters({ processed: result.totalFiles ?? 0, success: 0, failed: result.totalFiles ?? 0 });
            renderGlobalAlert({
                variant: "error",
                statusCode: 0,
                title: "Ошибка импорта",
                detail: result.generalErrors.join("; ")
            });
            importStats.current.failed += result.totalFiles ?? 0;
            return;
        }

        const errorMessages: string[] = [];
        const resultStatuses: Record<string, "success" | "error" | "bunch"> = {};
        let immediateSuccess = 0;
        let immediateFailed = 0;
        let immediateBunch = 0;
        const processedFileNames = new Set<string>();

        const findFileByName = (name: string): ILocalFileDTO | undefined => {
            let file = files.find(f => f.fileName === name);
            if (file) return file;
            const lower = name.toLowerCase();
            file = files.find(f => f.fileName.toLowerCase() === lower);
            if (file) return file;
            const baseName = name.replace(/\.[^/.]+$/, "").toLowerCase();
            file = files.find(f => f.fileName.replace(/\.[^/.]+$/, "").toLowerCase() === baseName);
            return file;
        };

        const findRowIdByFileName = (name: string): string | undefined => {
            const lower = name.toLowerCase();
            const baseName = name.replace(/\.[^/.]+$/, "").toLowerCase();
            for (const [key, id] of selectedIdByFileName.current.entries()) {
                const fileNamePart = key.split("|")[0];
                if (fileNamePart === name) return id;
                if (fileNamePart.toLowerCase() === lower) return id;
                if (fileNamePart.replace(/\.[^/.]+$/, "").toLowerCase() === baseName) return id;
            }
            return undefined;
        };

        for (const fileResult of result.results ?? []) {
            const resultFileName = fileResult.fileName ?? "";
            processedFileNames.add(resultFileName);
            const file = findFileByName(resultFileName);
            const rowId = file ? selectedIdByFileName.current.get(getImportRowKey(file)) : findRowIdByFileName(resultFileName);

            const hasUnmatched = (fileResult.typeBunches?.length ?? 0) > 0
                || fileResult.isPendingMatching === true
                || (!!fileResult.idImportedInvoiceGlobal && !fileResult.createdInvoiceGlobal);

            const shouldOpenBunch = skipBunchIfLinked ? hasUnmatched : !!fileResult.idImportedInvoiceGlobal;

            if (fileResult.createdInvoiceGlobal) {
                immediateSuccess++;
                if (rowId) {
                    resultStatuses[rowId] = "success";
                }
            } else if (shouldOpenBunch && fileResult.idImportedInvoiceGlobal) {
                // Сервер может вернуть isSucceed=false при наличии связок (неразрешенные товары),
                // поэтому ориентируемся на наличие typeBunches, а не на флаг isSucceed.
                if (file && rowId) {
                    immediateBunch++;
                    resultStatuses[rowId] = "bunch";
                    pendingBunchQueue.current.push({
                        file,
                        idImportedInvoiceGlobal: fileResult.idImportedInvoiceGlobal,
                        rowId,
                        typeBunches: fileResult.typeBunches,
                        errorsList: fileResult.errorsList
                    });
                } else {
                    immediateFailed++;
                    if (rowId) {
                        resultStatuses[rowId] = "error";
                    }
                    const availableNames = files.map(f => f.fileName).join(", ");
                    errorMessages.push(`${resultFileName}: не удалось сопоставить файл для ручной обработки (доступные файлы: ${availableNames})`);
                }
            } else {
                immediateFailed++;
                if (rowId) {
                    resultStatuses[rowId] = "error";
                }
                const fileErrors = fileResult.errorsList?.length
                    ? `${resultFileName}: ${fileResult.errorsList.join(", ")}`
                    : `${resultFileName}: не удалось импортировать`;
                errorMessages.push(fileErrors);
            }
        }

        files.forEach(file => {
            if (!processedFileNames.has(file.fileName) && !processedFileNames.has(file.fileName.toLowerCase())) {
                immediateFailed++;
                const rowId = selectedIdByFileName.current.get(getImportRowKey(file));
                if (rowId) {
                    resultStatuses[rowId] = "error";
                }
                errorMessages.push(`${file.fileName}: не удалось импортировать`);
            }
        });

        // Добавляем в очередь связок файлы, выбранные пользователем как требующие связки.
        for (const pendingFile of additionalPendingBunches) {
            const rowId = selectedIdByFileName.current.get(getImportRowKey(pendingFile));
            if (!rowId || !pendingFile.idImportedInvoiceGlobal) {
                immediateFailed++;
                errorMessages.push(`${pendingFile.fileName}: не удалось сопоставить файл для ручной обработки`);
                continue;
            }
            immediateBunch++;
            resultStatuses[rowId] = "bunch";
            pendingBunchQueue.current.push({
                file: pendingFile,
                idImportedInvoiceGlobal: pendingFile.idImportedInvoiceGlobal,
                rowId
            });
        }

        setRowImportStatus(prev => ({ ...prev, ...resultStatuses }));
        setProgressCounters(c => ({
            processed: c.processed + immediateSuccess + immediateFailed + immediateBunch,
            success: c.success + immediateSuccess,
            failed: c.failed + immediateFailed
        }));
        importStats.current = {
            success: importStats.current.success + immediateSuccess,
            failed: importStats.current.failed + immediateFailed
        };

        if (errorMessages.length > 0) {
            renderGlobalAlert({
                variant: "error",
                statusCode: 0,
                title: "Ошибки импорта",
                detail: errorMessages.join("; ")
            });
        }

        if (pendingBunchQueue.current.length > 0) {
            processNextBunch();
        } else {
            finishImport();
        }
    };

    const handleImport = () => {
        const files = getSelectedFiles();
        if (!canImportFiles(files)) return;

        setShowFilterErrors(true);
        if (!validateFilter(contractorFilter, storeFilter)) return;

        const selectedRows = multipleSelect && selectedItems.length > 0
            ? selectedItems
            : selectedItem
                ? [selectedItem]
                : [];

        const plainNewFiles = files.filter(f => f.status === LocalFileStatus.New && !f.idImportedInvoiceGlobal);
        const pendingMatchingFiles = files.filter(f => f.status === LocalFileStatus.New && !!f.idImportedInvoiceGlobal);

        importStats.current = { success: 0, failed: 0 };
        pendingBunchQueue.current = [];
        isCancelledRef.current = false;

        const actionableRows = selectedRows.filter(row => {
            const file = fileByIdGlobal.current.get(row.idGlobal);
            return !!file && file.status === LocalFileStatus.New;
        });

        const newStatus: Record<string, "processing"> = {};
        actionableRows.forEach(row => {
            newStatus[row.idGlobal] = "processing";
        });
        setRowImportStatus(newStatus);

        const rowIdByFileKey = new Map<string, string>(
            actionableRows
                .map((row): [string, string] => {
                    const file = fileByIdGlobal.current.get(row.idGlobal);
                    if (!file) return ["", ""];
                    return [getImportRowKey(mapDtoToLocalFile(file)), row.idGlobal];
                })
                .filter(([key]) => key !== "")
        );
        selectedIdByFileName.current = rowIdByFileKey;

        const actionableCount = plainNewFiles.length + pendingMatchingFiles.length;
        setTotalToProcess(actionableCount);
        setProgressCounters({ processed: 0, success: 0, failed: 0 });

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        setIsImporting(true);

        const storeId = storeFilter?.idGlobal ?? "";

        const runNewImport = () => {
            if (plainNewFiles.length === 0) {
                // Новых файлов нет — сразу обрабатываем выбранные файлы на связке.
                handleImportResult(
                    { isSucceed: true, totalFiles: 0, successCount: 0, failedCount: 0, bunchCount: 0, results: [], generalErrors: [] },
                    [],
                    pendingMatchingFiles
                );
                return;
            }
            dataProvider.importAndCreate(
                { files: plainNewFiles, store: storeId },
                (result) => {
                    if (isCancelledRef.current) {
                        return;
                    }
                    handleImportResult(result, plainNewFiles, pendingMatchingFiles);
                },
                abortControllerRef.current!.signal
            );
        };

        runNewImport();
    };

    const handleCancelImport = () => {
        isCancelledRef.current = true;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setIsImporting(false);
        setRowImportStatus({});
        setTotalToProcess(0);
        setProgressCounters({ processed: 0, success: 0, failed: 0 });
        pendingBunchQueue.current = [];
        importStats.current = { success: 0, failed: 0 };
    };



    const handleDelete = () => {
        const files = getSelectedFiles();
        if (files.length === 0) {
            renderGlobalAlert({
                variant: "warning",
                statusCode: 0,
                title: "Внимание",
                detail: "Выберите файлы для удаления"
            });
            return;
        }

        renderConfirmationModal(
            "Удаление файлов",
            `Будут удалены файлы (${files.length} шт.).`,
            files.map(f => f.fileName).join(", "),
            "Подтвердите удаление?",
            () => executeDelete(files)
        );
    };

    const executeDelete = (files: ILocalFileDTO[]) => {
        setConfirmationItemsModalJsx(<></>);
        dataProvider.deleteFilesLocal(files, (success) => {
            if (success) {
                setSelectedItems([]);
                refreshData()
                renderGlobalAlert({
                    variant: "success",
                    statusCode: 0,
                    title: "Успешно",
                    detail: "Файлы удалены"
                });
            } else {
                renderGlobalAlert({
                    variant: "error",
                    statusCode: 0,
                    title: "Ошибка",
                    detail: "Не удалось удалить файлы"
                });
            }
        });
    };

    const handleSelect = (row?: IGridRow) => {
        setSelectedItem(row);
        if (row) {
            openDetailsForRow(row);
        } else {
            setSelectedImportedInvoice(null);
            setShowDetailsTabs(false);
            pluginCtx.masterGrid.onUnselectEvent();
        }
    };

    const mapPreviewItemToImportedItem = (item: IImportInvoicePreviewItemDTO): IImportedInvoiceItemDTO => {
        return normalizeImportedInvoiceItem({
            idGlobal: uuidv4(),
            idGoodsExternalGlobal: item.goods?.idGlobal ?? "",
            goods: item.goods ?? { idGlobal: "", displayName: "" },
            goodsСode: item.goodsCode ?? (item as any).goodsСode ?? "",
            goodsName: item.goodsName ?? "",
            producer: item.producer ?? "",
            country: item.country ?? "",
            quantity: item.quantity ?? 0,
            supplierPrice: item.supplierCostInfo?.price ?? 0,
            supplierPriceWithOutVat: item.supplierCostInfo?.sum ?? 0,
            supplierCostInfo: item.supplierCostInfo,
            retailCostInfo: item.retailCostInfo,
            sumIncVat: item.supplierCostInfo?.sumIncVat ?? 0,
            sumWithoutVat: item.supplierCostInfo?.sum ?? 0,
            supplierVat: item.supplierCostInfo?.vat ?? 0,
            retailPriceIncVat: item.retailCostInfo?.priceIncVat ?? 0,
            barcode: item.barcode ?? "",
            isImportant: false,
            isKiz: false,
            gtin: "",
            kizs: [],
            certificates: [],
            scalingRatio: { idGlobal: "", displayName: "" },
            success: true,
            type: "",
            registerDateTime: DateTime.now(),
            registerPrice: 0,
            priceLBO: 0,
            itemPrice: 0,
            reservPrice: 0,
            wholesaleTradeMarkup: 0,
            expirationDateInMonths: 0,
            gtdNumber: "",
            seriesNumber: "",
            dateProduction: DateTime.now(),
            bestBefore: DateTime.now()
        } as unknown as IImportedInvoiceItemDTO);
    };

    const mapPreviewResultToImportedInvoice = (preview: IImportInvoiceResultDTO, file: IImportFileViewDTO): IInvoiceImportedDTO => {
        const items = (preview.items ?? []).map(mapPreviewItemToImportedItem);

        return {
            idGlobal: uuidv4(),
            idSupplierGlobal: file.idSupplierGlobal ?? "",
            idStoreGlobal: file.idStoreGlobal ?? "",
            supplierDocNum: preview.supplierDocNum ?? file.supplierDocNum ?? "",
            supplierDocDate: preview.supplierDocDate ? DateTime.fromISO(preview.supplierDocDate) : (file.supplierDocDate ?? DateTime.now()),
            supplier: { idGlobal: file.idSupplierGlobal ?? "", displayName: file.supplierName ?? "" },
            store: { idGlobal: file.idStoreGlobal ?? "", displayName: file.storeName ?? "" },
            supplierDocSumIncVat: preview.sumIncVat ?? 0,
            supplierDocType: "",
            invoiceVatSum10: 0,
            invoiceVatSum20: 0,
            supplierName: file.supplierName ?? "",
            storeName: file.storeName ?? "",
            payerName: "",
            recipientCode: 0,
            recipientName: "",
            orderNumber: "",
            buyerFio: "",
            payDate: "",
            kizAcceptionType: 0,
            kizCodeSup: "",
            contrNum: "",
            contrDate: "",
            deleted: false,
            dateDeleted: DateTime.now(),
            dateModified: DateTime.now(),
            pricingModel: { idGlobal: "", displayName: "" },
            items
        } as IInvoiceImportedDTO;
    };

    const openDetailsForRow = (row: IGridRow) => {
        setSelectedImportedInvoice(null);
        setShowDetailsTabs(false);

        const fileDto = fileByIdGlobal.current.get(row.idGlobal);
        const safeIdGlobal = fileDto?.idImportedInvoiceGlobal || fileDto?.idContractorGlobal || contractorFilter?.idGlobal || uuidv4();
        pluginCtx.masterGrid.onSelectEvent({ ...row, idGlobal: safeIdGlobal }, DocumentType.importInvoice);

        if (fileDto && fileDto.status === LocalFileStatus.Imported && fileDto.idInvoiceGlobal) {
            tabsCtx.openChild("invoice_creator_plugin", "view", fileDto.idInvoiceGlobal);
            return;
        }

        if (!fileDto) {
            return;
        }

        const isNewFile = fileDto.status === LocalFileStatus.New;

        // Ошибочные/удалённые файлы без импортированной накладной нечего детализировать.
        if (!isNewFile && !fileDto.idImportedInvoiceGlobal) {
            return;
        }

        // Новый файл, ещё не импортированный — показываем preview-позиции без необходимости выбирать склад/аптеку.
        if (isNewFile && !fileDto.idImportedInvoiceGlobal) {
            const file = mapDtoToLocalFile(fileDto);
            setSelectedImportedInvoiceLoading(true);
            dataProvider.preview(file, (result) => {
                setSelectedImportedInvoiceLoading(false);
                if (!result.isSucceed || result.errorsList.length > 0) {
                    renderGlobalAlert({
                        variant: "error",
                        statusCode: 0,
                        title: "Ошибка предпросмотра накладной",
                        detail: result.errorsList?.join("; ") || "Не удалось получить данные накладной"
                    });
                    return;
                }

                const invoice = mapPreviewResultToImportedInvoice(result, fileDto);
                setSelectedImportedInvoice(invoice);
                setShowDetailsTabs(true);
            });
            return;
        }

        // Файл с импортированной накладной (ожидает связки или уже связан) — показываем детали позиций.
        if (!fileDto.idImportedInvoiceGlobal) {
            return;
        }

        setSelectedImportedInvoiceLoading(true);
        importedInvoiceDataProvider.viewImportedInvoiceById(fileDto.idImportedInvoiceGlobal, (entity) => {
            setSelectedImportedInvoiceLoading(false);
            if (!entity) {
                renderGlobalAlert({
                    variant: "error",
                    statusCode: 0,
                    title: "Ошибка загрузки накладной",
                    detail: "Не удалось загрузить импортированную накладную"
                });
                return;
            }

            setSelectedImportedInvoice(entity as IInvoiceImportedDTO);
            setShowDetailsTabs(true);
        });
    };

    const handleMultipleSelect = (rows: IGridRow[], currentRow?: IGridRow) => {
        setSelectedItems(rows);
        if (!currentRow) {
            setSelectedRowId(null);
            setSelectedImportedInvoice(null);
            setShowDetailsTabs(false);
        }
    };

    const selectedFilesForImport = getSelectedFiles();
    const hasActionableFilesForImport = selectedFilesForImport.some(f => f.status === LocalFileStatus.New);
    const importButtonDisabled = isImporting
        || selectedFilesForImport.length === 0
        || !isDestinationSelected
        || !hasActionableFilesForImport;

    return (
        <>
            <PluginWrapper>
                <ToolbarWrapper>
                    <div className={localStyles.topFilteringPanel}>
                        <GlobalImportInvoicesFilteringPanel
                            datePeriod={datePeriod}
                            onDatePeriodChange={setDatePeriod}
                            status={statusFilter}
                            onStatusChange={setStatusFilter}
                            supplier={supplierFilter}
                            onSupplierChange={setSupplierFilter}
                            invoiceImportMappingModel={invoiceImportMappingModel}
                            onInvoiceImportMappingModelChange={handleInvoiceImportMappingModelChange}
                            isInvoiceSettingEnabled={isInvoiceSettingEnabled}
                            supplierImportProvider={supplierImportProvider}
                            contractor={contractorFilter}
                            onContractorChange={setContractorFilter}
                            store={storeFilter}
                            onStoreChange={setStoreFilter}
                            contractorError={showFilterErrors ? filterErrors.pharmacy : undefined}
                            storeError={showFilterErrors ? filterErrors.store : undefined}
                            searchText={searchText}
                            onSearchChange={setSearchText}
                            deleteProps={{
                                onClick: handleDelete,
                                disabled: getSelectedFiles().length === 0
                            }}
                            refreshProps={{
                                onClick: () => refreshData(),
                                disabled: isSubmitting
                            }}
                        />
                    </div>
                </ToolbarWrapper>

                {isImporting && (
                    <div className={localStyles.importStatusPanel}>
                        <div className={localStyles.importStatusInfo}>
                            <Spinner />
                            <span className={localStyles.importStatusTitle}>{t("importInvoice.globalImport.importingTitle")}</span>
                            <span className={localStyles.importStatusCounter}>
                                {t("importInvoice.globalImport.processedCount", { processed: progressCounters.processed, total: totalToProcess })}
                            </span>
                        </div>
                        <Button
                            variant="secondary"
                            className={localStyles.cancelButton}
                            onClick={handleCancelImport}
                        >
                            {t("importInvoice.globalImport.cancel")}
                        </Button>
                    </div>
                )}

                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}>
                        {isSubmitting ? (
                            <Spinner />
                        ) : (
                            <DefaultGrid
                                gridId={props.gridId}
                                data={gridData}
                                showQuantity
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                separator
                                filter={gridFilter}
                                totalCount={totalCount}
                                plugin={props.plugin}
                                multipleSelect={multipleSelect}
                                selectOnlyByCheckbox
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                saveFocus={[saveFocus, setSaveFocus]}
                                getView={(gridFilter, callback) => {
                                    const filter: IImportFilesFilterDTO = {
                                        ...getFilter(),
                                        pageNumber: gridFilter.pageNumber,
                                        numberPerPage: gridFilter.numberPerPage
                                    };

                                    dataProvider.getImportFilesView(filter, (entities, total) => {
                                        const enriched = entities.map(enrichFileDto);

                                        // Объединяем загруженные страницы, чтобы выбор/импорт работали
                                        // для строк, подгруженных скроллом.
                                        setData((prev) => {
                                            const map = new Map(prev.map((i) => [getImportRowKey(i), i]));
                                            enriched.forEach((i) => map.set(getImportRowKey(i), i));
                                            return Array.from(map.values()).sort((a, b) => {
                                                const aDate = a.supplierDocDate?.toMillis?.() ?? 0;
                                                const bDate = b.supplierDocDate?.toMillis?.() ?? 0;
                                                return bDate - aDate || a.fileName.localeCompare(b.fileName);
                                            });
                                        });
                                        setTotalCount(total);

                                        const rows = enriched.map(toGridRow);
                                        callback(rows, total);
                                        setSelectedItem(undefined);
                                        setSelectedImportedInvoice(null);
                                        setShowDetailsTabs(false);

                                        const filesToPreview = enriched
                                            .filter(
                                                (e) =>
                                                    e.status === LocalFileStatus.New &&
                                                    (e.itemCount == null || e.sumIncVat == null) &&
                                                    !previewRequestedRef.current.has(`${e.fileName}|${e.idContractorGlobal}|${e.connectionString}`)
                                            )
                                            .map(mapDtoToLocalFile);

                                        if (filesToPreview.length > 0) {
                                            filesToPreview.forEach((f) => previewRequestedRef.current.add(`${f.fileName}|${f.idContractorGlobal}|${f.connectionString}`));
                                            dataProvider.previewBatch(filesToPreview, () => refreshData());
                                        }
                                    });
                                }}
                                onSelect={(row) => handleSelect(row)}
                                onMultipleSelect={handleMultipleSelect}
                                onRowClick={(row) => {
                                    setSelectedRowId(row.idRow ?? null);
                                    openDetailsForRow(row);
                                }}
                                getStyleRow={(row) => {
                                    const classes: string[] = [];
                                    if (row.idRow === selectedRowId) {
                                        classes.push(localStyles.rowSelected);
                                    }
                                    const status = (row as any).importStatus;
                                    if (status === "success") classes.push(localStyles.rowSuccess);
                                    else if (status === "error") classes.push(localStyles.rowError);
                                    else if (status === "bunch") classes.push(localStyles.rowBunch);
                                    else {
                                        const matchingStatus = (row as any).matchingStatus;
                                        if (matchingStatus === "pending") classes.push(localStyles.rowMatchingPending);
                                    }
                                    return classes.length ? classes.join(" ") : null;
                                }}
                                getStyleCell={(cell, cells) => {
                                    const matching = cells.find((c) => c.propertyName === "matchingStatus");
                                    if (matching?.value === "pending") return localStyles.cellMatchingPending;
                                    return null;
                                }}
                                renderCustomCell={(cell, row) => {
                                    if (cell.propertyName === "importStatus") {
                                        const status = (row as any).importStatus;
                                        if (!status) return null;
                                        if (status === "processing") return <Spinner />;
                                        if (status === "success") return <CheckIcon className={localStyles.statusIconSuccess} />;
                                        if (status === "error") return <CrossIcon className={localStyles.statusIconError} />;
                                        if (status === "bunch") return <span title="Требуется ручная связка">⚠️</span>;
                                        return null;
                                    }
                                    if (cell.propertyName === "matchingStatus") {
                                        const status = (row as any).matchingStatus;
                                        if (status === "pending")
                                            return <span className={localStyles.matchingPending} title={t("documents.globalImportInvoices.matchingStatus.pending")}>⚠️ {t("documents.globalImportInvoices.matchingStatus.pending")}</span>;
                                        if (status === "done")
                                            return <span className={localStyles.matchingDone} title={t("documents.globalImportInvoices.matchingStatus.done")}>✓ {t("documents.globalImportInvoices.matchingStatus.done")}</span>;
                                        return <></>;
                                    }
                                    return null;
                                }}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            />
                        )}
                    </div>
                </div>

                <div className={localStyles.importButtonRow}>
                    <div className={localStyles.importButtonCell}>
                        <Checkbox
                            checked={skipBunchIfLinked}
                            onChange={(e) => setSkipBunchIfLinked(e.target.checked)}
                            disabled={isImporting}
                        >
                            {t("documents.globalImportInvoices.skipBunchIfLinked")}
                        </Checkbox>
                        <Button
                            className={localStyles.importButton}
                            variant="primary"
                            disabled={importButtonDisabled}
                            isLoadingSpinner={isImporting}
                            onClick={handleImport}
                        >
                            Импортировать
                        </Button>
                    </div>
                </div>

                {showDetailsTabs && selectedImportedInvoice && (
                    <ImportedInvoiceContext.Provider value={selectedImportedInvoice}>
                        <div className={styles.detailsTabView}>
                            <div className={tabsStyles.tabsPanelWrapper}>
                                <TabsPanel
                                    id="detailsTabsPanel"
                                    activeId={detailsTabsPanel.currentTab?.id}
                                    tabs={detailsTabsPanel.tabs}
                                    onActive={(id) => dispatchDetailsTabsPanel({ type: "activate", payload: id })}
                                />
                            </div>
                            <div className={tabsStyles.contentWrapper}>
                                {detailsTabsPanel.tabs.map((item) => (
                                    <div
                                        key={item.id}
                                        className={classNames(
                                            item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden
                                        )}
                                    >
                                        {item.view.content}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </ImportedInvoiceContext.Provider>
                )}

                {selectedImportedInvoiceLoading && <Spinner />}
                {confirmationItemsModalJsx}
            </PluginWrapper>
        </>

    );
};

export default GlobalImportInvoiceView;
